import type { Item, TaxRate } from '@/gen/cortado/v1/invoices_pb'
import { ItemUnitEnum } from '@/gen/cortado/v1/settings_pb'
import type { QuoteLineItem } from '@/components/quotes/quote-line-items-table'
import { dollarsToCents } from '@/lib/currency-utils'
import { calculateLineTotalCents } from '@/lib/line-item-calculations'

export function isTransportationLineItem(item: QuoteLineItem): boolean {
  return item.unit === ItemUnitEnum.ITEM_UNIT_PER_MILE
}

export function getTransportationLineItem(items: QuoteLineItem[]): QuoteLineItem | undefined {
  return items.find(isTransportationLineItem)
}

export function withoutTransportationLineItems(items: QuoteLineItem[]): QuoteLineItem[] {
  return items
    .filter((item) => !isTransportationLineItem(item))
    .map((item, index) => ({ ...item, sortOrder: index }))
}

export function replacePrimaryLineItems(
  currentItems: QuoteLineItem[],
  primaryItems: QuoteLineItem[],
): QuoteLineItem[] {
  const transportationItems = currentItems.filter(isTransportationLineItem)
  return [...primaryItems, ...transportationItems].map((item, index) => ({
    ...item,
    sortOrder: index,
  }))
}

export function replaceTransportationLineItem(
  items: QuoteLineItem[],
  transportationItem: QuoteLineItem,
): QuoteLineItem[] {
  return items.map((item) => (
    isTransportationLineItem(item) ? transportationItem : item
  )).map((item, index) => ({ ...item, sortOrder: index }))
}

export function applyTransportationMileageToLineItems({
  items,
  transportationItem,
  billableMiles,
  taxRates = [],
  autoApplyTaxRateId,
  createTempId = () => crypto.randomUUID(),
}: {
  items: QuoteLineItem[]
  transportationItem: Item
  billableMiles: number
  taxRates?: TaxRate[]
  autoApplyTaxRateId?: string
  createTempId?: () => string
}): QuoteLineItem[] {
  const matches = items.filter((item) => item.sourceItemId === transportationItem.id)
  if (matches.length > 1) {
    throw new Error('Duplicate Transportation lines must be resolved before recalculating mileage.')
  }

  if (billableMiles <= 0) {
    return items.filter((item) => item.sourceItemId !== transportationItem.id)
  }

  if (matches.length === 1) {
    return items.map((item) => {
      if (item.sourceItemId !== transportationItem.id) return item
      const lineTotalCents = calculateLineTotalCents(billableMiles, item.unitPriceCents)
      return {
        ...item,
        quantity: billableMiles,
        unit: ItemUnitEnum.ITEM_UNIT_PER_MILE,
        lineTotalCents,
        salesTaxCents: item.isTaxable && item.salesTaxRate
          ? Math.round(lineTotalCents * item.salesTaxRate)
          : 0,
      }
    })
  }

  const taxRate = taxRates.find((rate) => rate.id === autoApplyTaxRateId)
  const salesTaxRate = taxRate ? taxRate.percentage / 100 : undefined
  const unitPriceCents = dollarsToCents(transportationItem.price)
  const lineTotalCents = calculateLineTotalCents(billableMiles, unitPriceCents)
  return [...items, {
    tempId: createTempId(),
    sourceItemId: transportationItem.id,
    name: transportationItem.title,
    description: transportationItem.description || '',
    quantity: billableMiles,
    unitPriceCents,
    lineTotalCents,
    unit: ItemUnitEnum.ITEM_UNIT_PER_MILE,
    sortOrder: items.length,
    isTaxable: true,
    salesTaxRateId: taxRate?.id,
    salesTaxRateName: taxRate?.name,
    salesTaxRate,
    salesTaxCents: salesTaxRate ? Math.round(lineTotalCents * salesTaxRate) : 0,
  }]
}
